/**
 * HR Bulk Employee Import page — /hr/import
 *
 * Three-step flow:
 *   1. CSV upload (drag/drop or file picker)
 *   2. Dry-run preview — shows which rows will be created vs updated + errors
 *   3. Commit — writes to DB with per-row audit log
 *
 * Backend: POST /api/v1/hr/employees/bulk-import
 */

import { useRef, useState } from 'react'
import { VISUALLY_HIDDEN } from '../lib/visually-hidden'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { PreviewGrid } from './hr-bulk-import/PreviewGrid'
import {
  parseCsvToRows,
  type BulkImportResult,
  type BulkImportRow,
} from '../api/hr-bulk-import'

// ── Step indicator ────────────────────────────────────────────────────────────

type Step = 'upload' | 'preview' | 'done'

interface StepBadgeProps {
  step: 1 | 2 | 3
  label: string
  active: boolean
  done: boolean
}

function StepBadge({ step, label, active, done }: StepBadgeProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          fontSize: 12,
          fontWeight: 700,
          background: done
            ? 'var(--success-fg)'
            : active
              ? 'var(--steel-blue)'
              : 'var(--surface-2)',
          color: done || active ? 'var(--surface)' : 'var(--text-3)',
          border: active || done ? 'none' : '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {done ? '✓' : step}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: active ? 600 : 400,
          color: active ? 'var(--text)' : 'var(--text-3)',
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ── CSV upload step ───────────────────────────────────────────────────────────

interface UploadStepProps {
  onRowsParsed: (rows: BulkImportRow[]) => void
}

// Generic employee template. Columns may be in any order; unmapped columns
// (Nationality, …) are ignored. Dates are YYYY-MM-DD. Only Email is required
// as a column — every existing employee is matched by Internal Id / Email and
// PATCHED (only the cells you fill are written). On-probation is inferred from
// Confirmation Date (past = confirmed). Approver may be a given name / nickname
// — matched against existing + same-file staff. Role is left untouched unless
// a Role column is given (single value or a delimited list, e.g. hod;hr).
// NOTE: fictional sample data only — no real employee info.
const CSV_TEMPLATE = `Internal Id,First Name,Last Name,Email,Birth Date,Gender,Nationality,Marital Status,Confirmation Date,Race,Job Title,Employment Type,Role,Approver,Team,Joining Date,Active/Inactive
10001,Aida,Rahman,aida.rahman@example.com,1985-04-12,Female,Malaysian,Married,2018-01-15,Malay,Head of Operations,Full time,hod,Lara,Operations,2017-07-15,Active
10002,Wei,Jun Hao,weijun.hao@example.com,1990-09-03,Male,Malaysian,Single,2021-06-01,Chinese,Operations Manager,Full time,manager,Aida,Operations,2020-12-01,Active`

function UploadStep({ onRowsParsed }: UploadStepProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [parseErrors, setParseErrors] = useState<Array<{ line: number; message: string }>>([])
  const [dragOver, setDragOver] = useState(false)

  function processFile(file: File): void {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = typeof e.target?.result === 'string' ? e.target.result : ''
      const { rows, errors } = parseCsvToRows(text)
      setParseErrors(errors)
      if (errors.length === 0 && rows.length > 0) {
        onRowsParsed(rows)
      }
    }
    reader.readAsText(file)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload CSV file"
        data-testid="csv-drop-zone"
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          const file = e.dataTransfer.files[0]
          if (file) processFile(file)
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--steel-blue)' : 'var(--border)'}`,
          borderRadius: 12,
          padding: '40px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'var(--primary-bg)' : 'var(--surface)',
          transition: 'background 100ms, border-color 100ms',
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 12 }}>📂</div>
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', marginBottom: 6 }}>
          Drag a CSV file here, or click to browse
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
          Use your standard employee CSV (incl. Internal Id). Columns can be in any order; extra columns (Nationality, etc.) are ignored. Only an Email column is required — existing people are matched by Internal Id / Email and updated with just the cells you fill. New hires additionally need Name, Team, Job Title, Birth Date + Gender. Role is left untouched unless a Role column is present (single value or a delimited list like hod;hr); on-probation is inferred from Confirmation Date; missing teams are created automatically; Approver can be a name or nickname. Dates are YYYY-MM-DD.
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          aria-hidden="true"
          tabIndex={-1}
          style={VISUALLY_HIDDEN}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) processFile(file)
          }}
        />
      </div>

      {parseErrors.length > 0 ? (
        <div
          role="alert"
          data-testid="parse-errors"
          style={{
            padding: '12px 16px',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {parseErrors.length} parse error{parseErrors.length === 1 ? '' : 's'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {parseErrors.map((err) => (
              <li key={err.line}>
                Line {err.line}: {err.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <details>
        <summary
          style={{
            fontSize: 13,
            color: 'var(--text-3)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          Download CSV template
        </summary>
        <pre
          style={{
            marginTop: 8,
            padding: '12px',
            background: 'var(--surface-2)',
            borderRadius: 8,
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-2)',
            overflowX: 'auto',
          }}
        >
          {CSV_TEMPLATE}
        </pre>
        <button
          type="button"
          onClick={() => {
            const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'lms-employee-import-template.csv'
            a.click()
            URL.revokeObjectURL(url)
          }}
          style={{
            marginTop: 8,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Download template
        </button>
      </details>
    </div>
  )
}

// ── Done step ─────────────────────────────────────────────────────────────────

interface DoneStepProps {
  result: BulkImportResult
  onReset: () => void
}

function DoneStep({ result, onReset }: DoneStepProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          padding: '20px 24px',
          background: 'var(--success-bg)',
          border: '1px solid var(--success-line)',
          borderRadius: 10,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--success-fg)', marginBottom: 8 }}>
          Import complete
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Created {result.wouldCreate} employee{result.wouldCreate === 1 ? '' : 's'}.
          Updated {result.wouldUpdate} employee{result.wouldUpdate === 1 ? '' : 's'}.
          {result.errorCount > 0
            ? ` ${result.errorCount} row${result.errorCount === 1 ? '' : 's'} skipped due to errors.`
            : ''}
        </div>
      </div>
      <div>
        <button
          type="button"
          data-testid="import-again-btn"
          onClick={onReset}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Import another file
        </button>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function HrBulkImportInner(): JSX.Element {
  const [step, setStep] = useState<Step>('upload')
  const [parsedRows, setParsedRows] = useState<BulkImportRow[]>([])
  const [commitResult, setCommitResult] = useState<BulkImportResult | null>(null)

  function handleRowsParsed(rows: BulkImportRow[]): void {
    setParsedRows(rows)
    setStep('preview')
  }

  function handleCommitted(result: BulkImportResult): void {
    setCommitResult(result)
    setStep('done')
  }

  function handleReset(): void {
    setParsedRows([])
    setCommitResult(null)
    setStep('upload')
  }

  return (
    <>
      <PageHead
        eyebrow="HR"
        title="Bulk employee import"
        subtitle="Upload a CSV to create or update employee records. Preview before committing."
      />

      {/* Step indicator */}
      <div
        style={{
          display: 'flex',
          gap: 20,
          marginBottom: 24,
          padding: '12px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          flexWrap: 'wrap',
        }}
      >
        <StepBadge
          step={1}
          label="Upload CSV"
          active={step === 'upload'}
          done={step !== 'upload'}
        />
        <StepBadge
          step={2}
          label="Preview"
          active={step === 'preview'}
          done={step === 'done'}
        />
        <StepBadge
          step={3}
          label="Done"
          active={step === 'done'}
          done={false}
        />
      </div>

      <Card padding={24}>
        {step === 'upload' ? (
          <UploadStep onRowsParsed={handleRowsParsed} />
        ) : step === 'preview' ? (
          <PreviewGrid
            rows={parsedRows}
            onBack={() => setStep('upload')}
            onCommitted={handleCommitted}
          />
        ) : commitResult ? (
          <DoneStep result={commitResult} onReset={handleReset} />
        ) : null}
      </Card>
    </>
  )
}

export function HrBulkImportPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <HrBulkImportInner />
    </HrAccessGuard>
  )
}
